// Detailed case study for Rehab Squared.
// Structure: Hero (mobile montage) -> Problem/Solution -> At-a-Glance -> My Focus ->
// Three User Types (+ system flow map) -> The Work -> Key Decisions -> Outcome -> Reflection.
const IMG = "/case-studies/rehab";

const BNSI = "/case-studies/bnsi";
const DLG = "/case-studies/dialogue";
const FC = "/case-studies/fishcaddy";
const REHQ = "/case-studies/rehq";

export const detailedCaseStudies = {
  "bnsi-redesign": {
    id: "01",
    slug: "bnsi-redesign",
    client: "Bahamas National Statistical Institute",
    title: "BNSI — National Statistics Portal",
    subtitle:
      "Redesigning how a nation finds, reads, and trusts its official data.",
    intro:
      "The Bahamas National Statistical Institute is the country's official source for census, economic, and social data. I redesigned its public portal so that anyone — from a policymaker to a student — can find, read, and trust national statistics without needing to be a data expert. As the lead UX/UI designer, I restructured the site's information architecture and visual language so official statistics finally feel as credible and usable as the institution behind them.",

    // Overview strip — four screens shown as uniform tiles in one row.
    heroImages: [
      {
        src: `${BNSI}/home.webp`,
        alt: "BNSI redesigned homepage (top) — hero, key indicators, data resources & recent publications",
        label: "Homepage · top",
      },
      {
        src: `${BNSI}/home-bottom.webp`,
        alt: "BNSI redesigned homepage (continued) — commitment, news & updates, data partners & footer",
        label: "Homepage · continued",
      },
      {
        src: `${BNSI}/methods.webp`,
        alt: "Statistical Methods & Standards page — methodology, why standards matter, and key frameworks",
        label: "Methods & Standards",
      },
      {
        src: `${BNSI}/news-page.webp`,
        alt: "BNSI News & Announcements page — searchable newsroom with featured press release and latest news",
        label: "News & Announcements",
      },
    ],
    heroCaption:
      "A look across the redesigned BNSI portal — from the homepage through methods & standards and the newsroom. Click any screen to view it in full.",

    overview: {
      problem:
        "National statistics were spread across dense, hard-to-navigate pages. Finding a specific publication, understanding a term, or interpreting a chart took real effort — and the experience didn't reflect the credibility of the institution behind it. Many people gave up before they found what they came for.",
      solution:
        "A clear, hierarchy-driven portal built around how people actually look for data: a homepage that surfaces headline indicators, a searchable publications library, an interactive data dashboard, and plain-language support pages (glossary, FAQ, legislation) that make the numbers legible and trustworthy.",
    },

    details: [
      { k: "Role", v: "Lead UX/UI Designer" },
      { k: "Client", v: "Bahamas National Statistical Institute (BNSI)" },
      { k: "Product", v: "National statistics web portal & data dashboard" },
      { k: "Users", v: "Policymakers · Researchers · Businesses · Students · Journalists" },
      { k: "Tools", v: "Figma" },
      { k: "Timeline", v: "2024 · ~10 weeks" },
    ],

    focusKicker: "My focus",
    focusHeading: [
      { t: "Designing for" },
      { t: "clarity", a: true, s: "," },
      { t: "and trust." },
    ],
    focus:
      "My focus was making official data approachable without diluting its authority. That meant a strong information hierarchy, consistent patterns across every page, and support layers — a glossary, FAQ, and legislation context — that let non-experts interpret the numbers with confidence. I owned the information architecture and the core page templates end to end, from the homepage down to the support pages.",

    userTypes: {
      kicker: "Who it's for",
      heading: [
        { t: "Many audiences," },
        { t: "one source of truth", a: true, s: "." },
      ],
      intro:
        "The portal had to serve very different people looking for the same thing: reliable numbers they can act on.",
      roles: [
        {
          role: "Policymakers",
          desc: "Need headline indicators and official reports to shape and defend national decisions.",
        },
        {
          role: "Researchers & Academics",
          desc: "Dig into detailed datasets, methodology, and historical series for analysis.",
        },
        {
          role: "Businesses",
          desc: "Track economic and labour-market indicators to plan and invest with confidence.",
        },
        {
          role: "Public, Students & Journalists",
          desc: "Want quick, understandable answers and context they can cite and share.",
          highlight: true,
        },
      ],
    },

    work: [
      {
        heading: "Finding & reading the data",
        text: "The core of the portal: a searchable publications library, an interactive data dashboard, and the legislation that gives the numbers their authority. Tap any screen to view it in full.",
        layout: "gallery",
        images: [
          {
            src: `${BNSI}/publications.webp`,
            alt: "Publications and statistical reports page with search, filters, and categories",
            label: "Publications library",
            caption: "Searchable, filterable reports by topic, year & category.",
          },
          {
            src: `${BNSI}/data-dashboard.webp`,
            alt: "Census & Social Statistics data dashboard with multiple charts",
            label: "Data dashboard",
            caption: "Themed charts, each with its source and latest update.",
          },
          {
            src: `${BNSI}/legislation.webp`,
            alt: "Legislation page explaining the legal foundation of national statistics",
            label: "Legislation",
            caption: "The statutory foundation and governing documents.",
          },
        ],
      },
      {
        heading: "Understanding & staying informed",
        text: "Support pages that make the data legible for everyone — a plain-language glossary, a topic-grouped FAQ, and a clean template for announcing new findings.",
        layout: "gallery",
        images: [
          {
            src: `${BNSI}/glossary.webp`,
            alt: "Statistical glossary page organised alphabetically",
            label: "Glossary",
            caption: "Every key term defined in plain language, A–Z.",
          },
          {
            src: `${BNSI}/faq.webp`,
            alt: "Frequently Asked Questions page grouped by topic",
            label: "FAQ",
            caption: "Common questions grouped into popular topics.",
          },
          {
            src: `${BNSI}/news.webp`,
            alt: "Individual news / press release article template",
            label: "News / press release",
            caption: "A readable layout for releasing new data and reports.",
          },
        ],
      },
    ],

    keyDecisions: {
      kicker: "Key decisions",
      heading: [
        { t: "Decisions that" },
        { t: "built trust", a: true, s: "." },
      ],
      intro: "A few choices shaped how credible and usable the portal feels.",
      points: [
        {
          t: "Led with headline indicators",
          d: "Surfaced the four most-requested national figures on the homepage so value is visible in seconds.",
        },
        {
          t: "Made publications searchable, not scattered",
          d: "Replaced loose PDF links with a filterable library organised by topic, year, and category.",
        },
        {
          t: "Paired every chart with its source",
          d: "Each dashboard figure shows its source and last-updated date, making data citable and trustworthy.",
        },
        {
          t: "Added a plain-language support layer",
          d: "A glossary, FAQ, and legislation context let non-experts interpret data without specialist knowledge.",
        },
      ],
    },

    outcomeKicker: "Outcome",
    outcomeHeading: [
      { t: "A portal the" },
      { t: "nation can trust", a: true, s: "." },
    ],
    outcome:
      "The redesign turned a dense repository into a portal built around real questions — where headline data is instantly visible, publications are findable, and every number carries the context to be understood and trusted. Wayfinding is simpler, publications are searchable in a few clicks, and the portal now looks and behaves like a source a nation can rely on.",

    reflectionCtaLabel: "Start a project",
    reflection:
      "The hardest part of statistical design isn't the charts — it's trust. Getting people to believe a number means showing where it came from, what it means, and why it matters. On BNSI, the real design work was building that credibility into every screen.",
  },
  "dialogue": {
    id: "02",
    slug: "dialogue",
    client: "Dialogue",
    title: "Dialogue — Turning Books Into Action",
    subtitle:
      "An AI audiobook app that turns the wisdom inside books into personalized advice and action plans.",
    intro:
      "I joined a product already in motion. My role was to design its AI feature flows, define the UX logic behind new capabilities, and redesign an onboarding that wasn't showing users why the app was worth their time.",

    // Hero montage — strongest mobile mockups, phone-scale.
    heroMobile: [
      {
        src: `${DLG}/onboarding-plan.webp`,
        alt: "Dialogue onboarding — 'We found a great starting point for you' with a matched book and plan",
        label: "Onboarding · Your Plan",
      },
      {
        src: `${DLG}/challenge.webp`,
        alt: "Dialogue AI challenge — 'Perform an emotional audit' drawn from a book, with audio",
        label: "AI challenge",
      },
      {
        src: `${DLG}/paywall.webp`,
        alt: "Dialogue paywall — 'Continue your learning path' with 7-day free trial plans",
        label: "Trial / paywall",
      },
    ],

    overview: {
      problem:
        "The app had a powerful idea — personalized guidance drawn from books — but the experience didn't communicate it. The existing onboarding walked users through generic setup steps without ever showing the core value, so people arrived at the home screen with no idea what made Dialogue different from a standard audiobook app. The AI capabilities also needed clear, intuitive flows to feel usable rather than gimmicky.",
      solution:
        "I redesigned the onboarding to lead with value — showing users what Dialogue does for them early — and designed the AI feature flows that turn a book into personalized advice and action, alongside polishing the existing UI so the new capabilities felt native to the product.",
    },

    details: [
      { k: "Role", v: "UX Strategy · AI Feature Design · Onboarding Redesign" },
      { k: "Type", v: "Existing product, new features" },
      { k: "Platform", v: "iOS & Android" },
      { k: "Domain", v: "Audio / AI mobile app" },
      { k: "Tools", v: "Figma · FigJam · Maze" },
      { k: "Timeline", v: "2025 · ~8 weeks" },
    ],

    focusKicker: "My focus",
    focusHeading: [
      { t: "Making AI features" },
      { t: "feel effortless", a: true, s: "." },
    ],
    focus:
      "Designing the AI feature flows, defining the UX logic behind new capabilities, redesigning onboarding to lead with value, and polishing the existing design as new features were introduced — so everything shipped as one coherent, intuitive product.",

    work: [
      {
        heading: "Designing the AI feature flows",
        text: "The new capabilities that let users ask a book questions and receive personalized advice and action plans — I designed how these flows are entered, used, and returned from so they feel intuitive rather than technical.",
        layout: "phones",
        images: [
          {
            src: `${DLG}/newhome.webp`,
            alt: "Home with 'Personalized Learning path' ask box — 'I want to learn about…' with suggested questions",
            label: "Ask a book",
            caption:
              "The entry point: a natural-language prompt turns any goal into book-based guidance.",
          },
          {
            src: `${DLG}/challenge.webp`,
            alt: "AI-generated challenge from 'The Subtle Art of Not Giving a F*ck' with audio and Start Challenge",
            label: "Advice → action",
            caption:
              "The AI returns a concrete, personalized challenge — with audio guidance and a clear next step.",
          },
          {
            src: `${DLG}/reminders.webp`,
            alt: "Reminders settings — Action Plans, Saved Concepts, Challenges toggles with frequency control",
            label: "Keep it going",
            caption:
              "Lightweight controls bring users back to their action plans, saved concepts, and challenges.",
          },
        ],
      },
      {
        heading: "Redesigning onboarding to lead with value",
        text: "I reworked the first-run experience to demonstrate the core value up front instead of generic setup — from picking topics and books to a matched plan, habit reminders, and a value-framed trial. Shown here in order.",
        layout: "phones",
        images: [
          {
            src: `${DLG}/topics.webp`,
            alt: "Onboarding step 1 of 3 — 'What do you want to grow in?' topic picker",
            label: "1 · Pick your goals",
            caption:
              "Step 1 captures intent — the topics the feed will be personalized around.",
          },
          {
            src: `${DLG}/pickbooks.webp`,
            alt: "Onboarding — 'Pick a few books you like' book selection grid",
            label: "2 · Choose books",
            caption:
              "Taste selection makes the very first recommendations feel personal.",
          },
          {
            src: `${DLG}/onboarding-plan.webp`,
            alt: "Onboarding step 2 of 3 — matched book 'The Seven Principles' with a personalized plan",
            label: "3 · See the plan",
            caption:
              "A 98%-match book and tailored plan show the value before any configuration.",
          },
          {
            src: `${DLG}/habits.webp`,
            alt: "Onboarding — 'Turn Insights Into Habits' with saved key insight and smart reminder frequency",
            label: "4 · Turn insight into habit",
            caption:
              "Key insights auto-save and smart reminders reinforce them — value that compounds.",
          },
          {
            src: `${DLG}/ready.webp`,
            alt: "Onboarding step 3 of 3 — 'You're ready to start learning' with sign-in options",
            label: "5 · Ready to start",
            caption:
              "Account creation lands last — after the user has already seen what they're signing up for.",
          },
          {
            src: `${DLG}/paywall.webp`,
            alt: "Paywall — 'Continue your learning path', what you'll get, and 7-day free trial plans",
            label: "6 · Convert on value",
            caption:
              "The trial frames payment around continuing progress the user has already experienced.",
          },
        ],
      },
      {
        heading: "Polishing and extending the design",
        text: "Refining the existing UI and designing new, on-brand screens — including the value and positioning moments that make the product's promise clear — so every addition feels like part of one coherent experience.",
        layout: "split",
        support: {
          label: "New feature screens",
          text: "Two examples of extending the design language into fresh moments: a value-first intro that frames the core promise, and a positioning screen that shows why natural, podcast-style summaries beat both dense books and robotic audio.",
        },
        images: [
          {
            src: `${DLG}/learn-minutes.webp`,
            alt: "'Learn from books in minutes' intro — short podcast-style episodes, with Start Learning",
            label: "Value intro",
          },
          {
            src: `${DLG}/why-grow.webp`,
            alt: "'Why grow with Dialogue?' — Traditional Books vs Audio Summaries vs Podcast Summary",
            label: "Positioning",
          },
        ],
      },
    ],

    keyDecisions: {
      kicker: "Key decisions",
      heading: [{ t: "Decisions that" }, { t: "led with value", a: true, s: "." }],
      intro:
        "A few choices made the AI feel approachable and the product worth paying for.",
      points: [
        {
          t: "Led onboarding with value, not setup",
          d: "Restructured the first-run flow so users see what Dialogue does for them before any configuration.",
        },
        {
          t: "Made AI features conversational, not technical",
          d: "Designed the 'ask a book' flows around natural, guided interactions so the AI felt approachable rather than intimidating.",
        },
        {
          t: "Kept new capabilities native to the product",
          d: "Every new feature screen matched the existing design language, so additions felt built-in rather than bolted on.",
        },
        {
          t: "Reduced friction to first value",
          d: "Cut unnecessary steps between opening the app and experiencing a personalized result.",
        },
      ],
    },

    outcomeKicker: "Outcome",
    outcomeHeading: [{ t: "Value, understood" }, { t: "faster", a: true, s: "." }],
    outcome:
      "The redesigned onboarding and clearer feature flows helped users understand the product's value faster — reducing early drop-off, improving onboarding completion, and lifting conversion to paid. New users reached a meaningful 'first result' moment sooner, and the AI features shipped as a coherent, intuitive part of the experience rather than an add-on.",
    outcomeStats: [
      { v: "~25%", k: "lower bounce on first session" },
      { v: "Improved", k: "onboarding completion" },
      { v: "Higher", k: "trial-to-paid conversion" },
    ],

    reflectionCtaLabel: "Start a project",
    reflection:
      "This project shows my strength in bringing clarity and usable structure to AI features inside a live product — leading with user value and making new capabilities feel effortless.",
  },
  "rehab-squared-pro": {
    id: "03",
    slug: "rehab-squared-pro",
    client: "Rehab Squared (RH2)",
    title: "Rehab Squared",
    subtitle:
      "Bringing operational clarity to a multi-sided renovation platform.",
    intro:
      "As UX consultant, I broke down and optimized the flows connecting three user types — homeowners, contractors, and coaches — with a strong focus on the operational and business logic that keeps a complex renovation project moving from first bid to final payment.",

    // Mobile app-screen montage — phone-scale, evenly spaced, stacks on mobile.
    heroMobile: [
      { src: `${IMG}/sow.png`, alt: "Scope of Work setup", label: "SOW setup" },
      { src: `${IMG}/accounting.png`, alt: "Accounting / payables", label: "Accounting" },
      { src: `${IMG}/resident-profile.png`, alt: "Resident profile", label: "Resident profile" },
    ],

    overview: {
      problem:
        "Rehab Squared connects homeowners, contractors, and coaches on one platform — but the flows tying these roles together, and the operational logic underneath them (bidding, scopes of work, estimates, milestones, payments, timelines), weren't clearly defined or shared. It was hard to see how a project actually moved from first request to final payment, or how each role handed off to the next.",
      solution:
        "I broke the platform down with the client, validated all three user types and how they relate, and optimized the business and operational logic — mapping the full ecosystem and translating it into flows and dashboards that reflect how the business actually runs, with a strong focus on the coach (project manager) and the RH2 operational roles.",
    },

    details: [
      { k: "Role", v: "UX Consultant (flow optimization + design)" },
      { k: "Client", v: "Rehab Squared (RH2)" },
      { k: "Product", v: "Multi-sided renovation-management SaaS" },
      { k: "Users", v: "Homeowners · Contractors · Coaches · RH2 ops staff" },
      { k: "Tools", v: "Figma" },
      { k: "Timeline", v: "2024 · ~12 weeks" },
    ],

    focus:
      "I worked directly with the client to break down the platform and make sure all three user types existed as coherent, well-understood roles — and that the relationships between them held together. My strongest focus was on the parts most teams skip: the coaches as project managers and the RH2 operational staff who run the backend, the transactions, and the project timeline. Rather than treating this as a UI polish job, I optimized the business aspect of the platform — the flow of a project through bids, estimates, SOWs, milestones, and payments — so the product reflected how the business actually needs to run.",

    userTypes: {
      intro:
        "Before optimizing anything, I made sure each role — and the relationships between them — was clearly defined.",
      roles: [
        {
          role: "Homeowner",
          desc: "Wants confidence their renovation is managed well from start to finish — transparent pricing, clear steps, and regular updates.",
        },
        {
          role: "Contractor (Pro / Specialist)",
          desc: "Bids on qualified projects, builds estimates, and executes the work.",
        },
        {
          role: "Coach (Project Manager)",
          desc: "The operational spine — qualifies projects, creates the scope of work, assembles the team, and steers the homeowner through milestones and approvals.",
          highlight: true,
        },
        {
          role: "RH2 Staff (Operations)",
          desc: "The backend — organization, transactions (markup, fees, contingency), and timeline management that keep projects on track.",
        },
      ],
      map: {
        src: `${IMG}/system-flow.webp`,
        alt: "Renovation neighbourhood user journey — system flow map",
        caption:
          "The full renovation journey mapped across every role — residents, specialists, and coaches — including BIDBIN bidding, qualification, SOW creation, estimates, and proposal approval.",
      },
    },

    work: [
      {
        heading: "Grounding it in the homeowner's experience",
        text: "Alongside the system view, I mapped the homeowner journey stage by stage — awareness, onboarding, planning, active project, and wrap-up — to keep the operational logic anchored to what the resident needs to feel confident at each step.",
        layout: "full",
        images: [
          {
            src: `${IMG}/homeowner-journey.webp`,
            alt: "Homeowner journey map",
            caption:
              "The homeowner journey stage by stage — awareness, onboarding, planning, active project, and wrap-up.",
          },
        ],
      },
      {
        heading: "Translating operational logic into the product",
        text: "With the flows defined, I moved into the screens where the business logic becomes real. On the web, role-aware dashboards let coaches and RH2 staff track phases, payables, and a project's full state, while a guided multi-step wizard turns a homeowner's raw property into a structured project. On mobile, contractors build the scope of work and stay on top of activity from the field.",
        layout: "ops",
        desktop: [
          {
            src: `${IMG}/project-overview.webp`,
            alt: "Project dashboard — the operational hub",
            caption:
              "The operational hub — the web dashboard where every role sees a project's full state, from bids to milestones to payments.",
          },
          {
            src: `${IMG}/phases.webp`,
            alt: "Manage project — phases & milestones",
            caption:
              "Phases & milestones — timeline, cost tracking, and job status for each active project.",
          },
        ],
        setup: [
          {
            src: `${IMG}/create-property-info.webp`,
            alt: "Create a Project — Step 1, Property Information",
            step: "Step 1",
            label: "Property Information",
            caption:
              "The guided wizard opens by capturing the property itself — address, type, size, and attributes — the foundation every later step builds on.",
          },
          {
            src: `${IMG}/create-management-prefs.webp`,
            alt: "Create a Project — Step 6, Management Preferences",
            step: "Step 6",
            label: "Management Preferences",
            caption:
              "The homeowner chooses whether a coach runs the project and books a time to connect — the moment the coach becomes the operational hub.",
          },
        ],
        mobile: [
          {
            src: `${IMG}/sow-materials.png`,
            alt: "Set Up SOW — Specify Materials",
            label: "SOW · Specify Materials",
            caption: "Contractors attach materials to each affected area.",
          },
          {
            src: `${IMG}/calendar.png`,
            alt: "Activity history / calendar",
            label: "Activity history",
            caption: "Project events and updates, on the go.",
          },
          {
            src: `${IMG}/project-finder.png`,
            alt: "Project finder",
            label: "Project finder",
            caption: "Browse and filter active jobs anywhere.",
          },
        ],
      },
    ],

    keyDecisions: {
      intro:
        "A few decisions shaped how the platform works, not just how it looks.",
      points: [
        {
          t: "Centered the coach as the operational hub",
          d: "The flow is built around the coach qualifying, scoping, and steering each project, rather than a self-serve marketplace.",
        },
        {
          t: "Surfaced operational and transactional state",
          d: "Invoice statuses, estimated-vs-invoiced, phases, and activity history — so every role can see exactly where a project stands.",
        },
        {
          t: "Structured project setup as a guided, staged flow",
          d: "SOW setup and property/project creation broken into clear steps with distinct empty and filled states.",
        },
        {
          t: "Kept the homeowner anchored throughout",
          d: "Mapped their journey stage by stage so operational complexity never left them unsure of the next step.",
        },
      ],
    },

    outcome:
      "The result was a shared, validated model of how three user types move through a renovation together, and a set of flows and screens that reflect the operational and transactional reality the platform runs on — not just how it looks, but how it works as a business. That shared model became the reference the team designed and built against.",

    reflection:
      "The interesting work on Rehab Squared wasn't the interface — it was the business underneath it. Clarifying how coaches, contractors, homeowners, and operational staff relate to each other was what unlocked the design. On complex platforms, the highest-leverage UX work is often defining the system, not decorating it.",
  },

  "fishcaddy-ux-audit": {
    id: "04",
    slug: "fishcaddy-ux-audit",
    client: "FishCaddy",
    title: "FishCaddy — UX Audit",
    subtitle:
      "A close read on where a fishing-report platform loses, and keeps, its users.",
    intro:
      "FishCaddy is a fishing-report platform where anglers track stockings, log reports, and follow their favorite waters across California. I ran a focused UX audit of the key user-facing surfaces — landing page, dashboard, onboarding, location pages, navigation, and the newsletter — reviewing UI clarity, flow, and engagement. The goal: increase engagement through clearer calls to action and onboarding, make navigation easier so people find content fast, and reduce the friction that quietly costs conversions.",

    heroImages: [
      {
        src: `${FC}/homepage.png`,
        alt: "FishCaddy website landing page — hero search, Latest Fishing Reports and Recently Stocked Waters",
        label: "Homepage",
      },
      {
        src: `${FC}/onboarding.png`,
        alt: "FishCaddy onboarding screen with numbered audit annotations — Follow Your Favorite Fishing Spots",
        label: "Onboarding · annotated",
      },
      {
        src: `${FC}/location-kern.png`,
        alt: "FishCaddy location page for Kern River with numbered audit annotations — overview, bite meter, weather, map",
        label: "Location page · annotated",
      },
      {
        src: `${FC}/newsletter-hero.png`,
        alt: "FishCaddy weekly newsletter hero — Get our CA Weekly Fishing Digest",
        label: "Newsletter",
      },
    ],
    heroCaption:
      "A focused review across FishCaddy's key surfaces — landing, dashboard, onboarding, location pages, and the newsletter. Click any screen to view the annotated audit in full.",

    overview: {
      problem:
        "The product works, but it leaks users. Key pages like counties and lakes are buried in scrollable lists instead of navigation, onboarding under-sells its value, and calls to action are weak or missing hover cues. Red is used where there's no error, empty states are passive, and near-identical card layouts for 'Recently Stocked Waters' and 'Latest Fishing Reports' create cognitive fatigue. The demo lands — the second session doesn't.",
      solution:
        "A prioritized set of fixes that raise engagement without a rebuild: surface key pages through clearer primary and secondary navigation, strengthen onboarding and empty states, restyle the red 'alert' banners into positive CTAs, differentiate the card sections, and add actionable CTAs (Follow, Get Alerts, Plan Trip) that turn static lists into things people do. Each finding is ranked by impact vs. effort so the team can start with the quick wins.",
    },

    details: [
      { k: "Role", v: "UX Auditor (UX Consultant)" },
      { k: "Client", v: "FishCaddy" },
      { k: "Product", v: "Fishing-report & stocking-tracker web platform" },
      { k: "Users", v: "Anglers tracking stockings, reports & favorite waters" },
      { k: "Scope", v: "Landing · Dashboard · Onboarding · Location pages · Navigation · Newsletter" },
      { k: "Deliverable", v: "Annotated audit + Impact vs. Effort matrix" },
    ],

    focusKicker: "Audit focus",
    focusHeading: [
      { t: "Find where the" },
      { t: "product loses people", a: true, s: "." },
    ],
    focus:
      "I ran FishCaddy's real flows the way a new user would and wrote down every place they'd stall, hesitate, or quietly give up — onboarding, navigation, empty states, error recovery, and the engagement layer. The point wasn't a list of opinions; it was a prioritized, honest read on what's costing the most, so a small team can fix the highest-leverage things first.",

    work: [
      {
        heading: "The annotated audit",
        text: "I marked up FishCaddy's key screens with specific, actionable findings — not vague advice. Onboarding that under-sells its value, location pages with duplicate CTAs and inconsistent hierarchy, a homepage where two card sections blur together, and a newsletter that reads as one dense block. Tap any screen to view it in full.",
        layout: "gallery",
        images: [
          {
            src: `${FC}/onboarding.png`,
            alt: "Onboarding screen annotated — de-emphasize Skip, strengthen value, add sorting, stronger Continue CTA",
            label: "Onboarding",
            caption: "De-emphasize 'Skip for now', surface social proof, add sorting, and make the 'Continue to Dashboard' CTA benefit-driven.",
          },
          {
            src: `${FC}/location-kern.png`,
            alt: "Location page annotated — collapse long description, standardize widgets, single CTA, surface reports",
            label: "Location page",
            caption: "Collapse long copy behind 'Read more', standardize the side widgets, keep one primary 'Log Report' CTA, and lift reports higher.",
          },
          {
            src: `${FC}/homepage.png`,
            alt: "Homepage annotated — sharpen hero, differentiate the two near-identical card sections, fix newsletter banner",
            label: "Homepage",
            caption: "'Recently Stocked Waters' and 'Latest Fishing Reports' use near-identical cards — differentiate them, and sharpen the hero's value + secondary CTA.",
          },
          {
            src: `${FC}/location-sonoma.png`,
            alt: "Location page tabs annotated — nearby lakes section title inconsistent with rest of the page",
            label: "Location · tabs",
            caption: "Standardize section titles and utility widgets so tabs and 'Nearby Lakes & Rivers' feel part of one page, not bolted on.",
          },
          {
            src: `${FC}/newsletter-hero.png`,
            alt: "Newsletter hero — checkmark icon implies already subscribed; restyle banner",
            label: "Newsletter · hero",
            caption: "The checkmark implies the user is already signed up — replace with an inviting visual and integrate the banner into the flow.",
          },
          {
            src: `${FC}/newsletter-reports.png`,
            alt: "Newsletter content — text-heavy sections need dividers, summaries and a mid-newsletter CTA",
            label: "Newsletter · content",
            caption: "Break dense sections with dividers and summaries, and add a mid-newsletter 'Log Your Report' CTA — not just one at the end.",
          },
        ],
      },
    ],

    keyDecisions: {
      kicker: "Impact vs. effort",
      heading: [{ t: "The quick wins," }, { t: "first", a: true, s: "." }],
      intro:
        "Every finding was mapped by effort required against impact expected, grouped into Navigation & Structure, Engagement & Personalization, and Content & Layout. The high-impact, low-effort moves come first — the fastest path to a more engaging product.",
      points: [
        {
          t: "Retire the false 'error' red",
          d: "Red is used on banners and labels where there's no error, creating tension and confusion. Reserve red for true alerts; restyle those banners into positive, branded CTA cards.",
        },
        {
          t: "Make CTAs feel clickable",
          d: "Comment, Share and many actions have no hover states. Consistent hover styling and real actions (Follow, Get Alerts, Plan Trip) turn passive lists into engagement.",
        },
        {
          t: "Rescue the empty states",
          d: "Plain 'Not following' and empty 'My Reports' pages waste the moment. Friendly illustrations, benefit copy, and a clear first action motivate the first real result.",
        },
        {
          t: "Surface the hidden pages",
          d: "Counties and lakes are buried in the hamburger menu and scroll. A clearer primary + secondary nav (mega-menu) makes the hierarchy obvious and pages reachable anywhere.",
        },
      ],
    },

    outcome:
      "The audit gave FishCaddy a single, prioritized picture of where the journey breaks: fragmented onboarding and navigation that leave new users unoriented, missed engagement moments from homepage to dashboard to reports, and content-heavy sections that blur together without hierarchy. Crucially, several of the highest-impact fixes — onboarding banners, empty states, filters, and CTA restyling — are low-effort, so the team has an efficient path to lift engagement and clarity quickly, before investing in deeper enhancements.",

    reflection:
      "A good audit isn't a list of opinions — it's a ranked, honest read on where a product loses people and what it costs. FishCaddy didn't need a redesign; it needed the leaks named and prioritized. The most valuable thing I can hand a small team is knowing exactly what to fix first.",
  },

  "rehq-crm": {
    id: "06",
    slug: "rehq-crm",
    client: "REHQ",
    title: "REHQ — a CRM shaped around how brokers actually work",
    subtitle:
      "Dashboard and deal management UI for a commercial real estate platform.",
    intro:
      "REHQ is a CRM for commercial real estate brokers. I designed the UI for its core surfaces — the dashboard, pipeline, deal records, contacts and property records — around a single idea: the tool's structure should follow the work, not force every deal into the same shape. The live product has since evolved beyond this work.",

    heroImages: [
      {
        src: `${REHQ}/dashboard.webp`,
        alt: "REHQ dashboard — tasks, a commissions breakdown, a pipeline snapshot and recent contacts",
        label: "Dashboard",
      },
      {
        src: `${REHQ}/pipeline.webp`,
        alt: "REHQ pipeline split into Active Deals and Prospective Deals with different columns",
        label: "Pipeline",
      },
      {
        src: `${REHQ}/deal.webp`,
        alt: "REHQ deal record for a buyer representation, with linked people and linked properties",
        label: "Deal record — buyer representation",
      },
      {
        src: `${REHQ}/contact.webp`,
        alt: "REHQ new-contact form with industry contact types tagged as distinct chips",
        label: "Contact creation",
      },
    ],
    heroCaption:
      "A tour across REHQ's core surfaces — the dashboard, pipeline, deal record and contact flow. Click any screen to view it in full.",

    overview: {
      problem:
        "Generic CRMs treat every deal the same. Commercial real estate doesn't work that way — a buyer representation and a landlord listing need different information at different moments, so an off-the-shelf tool buries brokers in fields that mostly don't apply.",
      solution:
        "A CRM whose structure follows the work. The deal record changes shape by deal type, the pipeline splits active from prospective deals with columns matched to each stage, contacts use an industry-specific schema, and property records carry the fields commercial brokers actually use.",
    },

    details: [
      { k: "Role", v: "UI Designer" },
      { k: "Client", v: "REHQ (commercial real estate CRM)" },
      { k: "Product", v: "CRM — dashboard, pipeline & deal management (web app)" },
      { k: "Scope", v: "Dashboard · Pipeline · Deal records · Contacts · Property records" },
      { k: "Tools", v: "Figma" },
      { k: "Note", v: "The live product has since evolved beyond this work." },
    ],

    focusKicker: "My focus",
    focusHeading: [
      { t: "Structure that" },
      { t: "follows the deal", a: true, s: "." },
    ],
    focus:
      "Rather than one universal form, I designed each surface around the decision a broker is actually making at that moment — which fields matter, which relationships to capture, and when. The result is a CRM that feels built for commercial real estate instead of adapted to it.",

    work: [
      {
        heading: "A workspace that opens on what matters",
        text: "The dashboard leads with the things a broker checks first — tasks due, a commissions breakdown by stage, a live pipeline snapshot and recent contacts — so the day's priorities are visible the moment they log in.",
        layout: "full",
        images: [
          {
            src: `${REHQ}/dashboard.webp`,
            alt: "REHQ dashboard with tasks, a commissions donut chart, a pipeline table and recent contacts",
            caption:
              "Dashboard — tasks, commissions by stage, a pipeline snapshot and recent contacts in one view.",
          },
        ],
      },
      {
        heading: "Active and prospective deals, each with their own columns",
        text: "The pipeline separates active deals from prospective ones and gives each different columns. Active deals surface due-diligence expiry, closing date and gross commission — the dates a broker manages against. Prospective deals surface city, total square footage and contact, because at that stage the question is only whether the deal is worth pursuing.",
        layout: "full",
        images: [
          {
            src: `${REHQ}/pipeline.webp`,
            alt: "REHQ pipeline with an Active Deals table and a separate Prospective Deals table, each with tailored columns",
            caption:
              "Pipeline — active deals track DD expiry, closing and gross commission; prospective deals track city, total SF and contact.",
          },
        ],
      },
      {
        heading: "The deal record changes shape by deal type",
        text: "Instead of one universal form full of fields that mostly don't apply, the deal record's shape follows the deal type. Linked people and linked properties sit inside the record itself, so relationships get made at the moment of work rather than through a separate association step.",
        layout: "full",
        images: [
          {
            src: `${REHQ}/deal.webp`,
            alt: "REHQ buyer representation deal record with tasks, notes, linked people and linked properties",
            caption:
              "Deal record — buyer representation. Linked people and properties live inside the record, captured in context.",
          },
        ],
      },
      {
        heading: "Contacts tagged by who they actually are",
        text: "Contacts use a schema built for the industry rather than a generic one. Property managers, surveyors, elected officials and contractors are tagged as distinct types, because who someone is determines when a broker needs them.",
        layout: "full",
        images: [
          {
            src: `${REHQ}/contact.webp`,
            alt: "REHQ new-contact form with contact types Property Manager, Surveyor, Elected Official and Contractor",
            caption:
              "Contact creation — industry contact types (property manager, surveyor, elected official, contractor) as distinct tags.",
          },
        ],
      },
      {
        heading: "Property records with the fields brokers actually use",
        text: "Property records carry the fields commercial brokers actually work with: zoning and future land use, ceiling heights, heated versus total square footage, tenancy and construction type — the data that decides whether a space fits a deal.",
        layout: "aside",
        images: [
          {
            src: `${REHQ}/property-detail.webp`,
            alt: "REHQ property detail with zoning/FLU, ceiling heights, heated and total SF, tenancy and construction type",
            caption:
              "Property detail — zoning/FLU, ceiling heights, heated vs. total SF, tenancy and construction type.",
          },
        ],
      },
    ],

    keyDecisions: {
      kicker: "Key decisions",
      heading: [{ t: "Decisions that" }, { t: "fit the industry", a: true, s: "." }],
      intro:
        "A few structural choices are what make REHQ feel built for commercial real estate rather than adapted to it.",
      points: [
        {
          t: "The deal record follows the deal type",
          d: "Rather than one universal form with fields that mostly don't apply, the record's shape changes with the deal type — a buyer rep and a landlord listing each show what matters.",
        },
        {
          t: "Two pipelines, two column sets",
          d: "Active deals surface DD expiry, closing date and gross commission; prospective deals surface city, total SF and contact — the question each stage is really asking.",
        },
        {
          t: "An industry contact schema",
          d: "Property managers, surveyors, elected officials and contractors are tagged as distinct types, because who someone is determines when a broker needs them.",
        },
        {
          t: "Relationships made in context",
          d: "Linked people and linked properties live inside the deal record, so connections are captured at the moment of work — not through a separate association step.",
        },
      ],
    },

    outcomeKicker: "Outcome",
    outcomeHeading: [{ t: "A CRM that" }, { t: "fits the work", a: true, s: "." }],
    outcome:
      "REHQ's UI reflects how commercial brokers actually operate — deals that show the right fields, a pipeline that answers the right question at each stage, contacts and properties modelled on the industry, and relationships captured where the work happens. The live product has since evolved beyond this work, but this design set its structural foundation.",

    reflection:
      "The most useful thing UI can do for a specialist tool is refuse the generic default. On REHQ, almost every decision came back to one question — what does this specific deal, at this specific moment, actually need on screen? Designing to that, rather than to a universal form, is what made it feel like a broker's tool.",
  },
};

export const getCaseStudy = (slug) => detailedCaseStudies[slug];
